import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Grid, useTheme, Collapse, Link, Box, IconButton, Typography, TextField } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SaveIcon from '@mui/icons-material/Save'
import FilterListIcon from '@mui/icons-material/FilterList'
import ClearIcon from '@mui/icons-material/Clear'
import {
  generateFilters,
  updateSelectedFilters,
  getSelectedFiltersFromQueryString,
} from './helpers'
import { filterDefinitions, UserType, filterGroups } from './filterDefinitions'
import { useIdentity } from 'pages/Identity/Login'
import { useStyles } from './styles'
import { SavedFiltersButton } from './SavedFiltersButton'
import AlertsButton from './AlertsButton'
import { FilterHeader } from './FilterHeader'
import { MobileFilterDropdown } from './FilterDropdown/MobileFilterDropdown'
import { DesktopFilterDropdown } from './FilterDropdown/DesktopFilterDropdown'
import DesktopCompositeFilterDropdown from './FilterDropdown/DesktopCompositeFilterDropdown'
import { fetchUserFilterAlerts, fetchFilterTypes } from '../../redux/filters'
import PhysicianSignUpDialog from '../../components/Dialog/PhysicianSignUpDialog'
import SaveFilterDialog from '../Dialog/SaveFilterDialog'
import MobileCompositeFilterDropdown from './FilterDropdown/MobileCompositeFilterDropdown'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'

export const AuthenticatedFilters = ({ filterType, filterValues, listType, applyFilters }) => {
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const styles = useStyles({ mobile })
  const { userFilterAlerts = [], filterTypes } = useSelector(state => state.filters)
  const { signedIn } = useSelector(state => state.identity)
  let ref = useRef(null)
  const { employerId, isEmployerAdmin, userId, employerUserId, candidateId } = useIdentity()
  const userType = employerId || isEmployerAdmin ? UserType.EMPLOYER : UserType.CANDIDATE
  const [searchParams, setSearchParams] = useSearchParams()

  const [selectedFilters, setSelectedFilters] = useState({})
  const [showAllFilters, setShowAllFilters] = useState(false)
  const [isFilterSectionOpen, setIsFilterSectionOpen] = useState(!mobile) // Default closed on mobile, open on desktop
  const filters = generateFilters(filterType, userType, filterValues)
  const dispatch = useDispatch()
  const userFilterAlertParam = searchParams.get('userFilterAlertId')
  const [openSaveDialog, setOpenSaveDialog] = useState(false)
  const [openMoreDialog, setOpenMoreDialog] = useState(false)
  const [openSaveFilterDialog, setOpenSaveFilterDialog] = useState(false)
  const isInitialMountRef = useRef(true)

  useEffect(() => {
    dispatch(fetchFilterTypes())
  }, [])

  // Ensure More Filters section is collapsed on mobile
  useEffect(() => {
    if (mobile) {
      setShowAllFilters(false)
    }
  }, [mobile])

  useEffect(() => {
    const filtersFromQuery = getSelectedFiltersFromQueryString(searchParams, filterValues)
    setSelectedFilters(prevFilters =>
      JSON.stringify(prevFilters) !== JSON.stringify(filtersFromQuery)
        ? filtersFromQuery
        : prevFilters,
    )
  }, [filterValues, searchParams])

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      return
    }
    
    applyFilters({ searchParams, employerId, employerUserId, candidateId, isEmployerAdmin })
    // Only auto-expand More Filters on desktop, not mobile
    if (!mobile) {
      Object.keys(selectedFilters).forEach(key => {
        const filterDef = filterDefinitions.find(filter => filter.name === key)
        if (filterDef && filterDef.showMoreFilters) {
          setShowAllFilters(true)
          return true
        }
      })
    }
  }, [selectedFilters, mobile])

  useEffect(() => {
    dispatch(fetchUserFilterAlerts(userId))
  }, [userId])

  const prevAlertParamRef = useRef(userFilterAlertParam)
  
  useEffect(() => {
    if (prevAlertParamRef.current !== userFilterAlertParam) {
      prevAlertParamRef.current = userFilterAlertParam
      applyFilters({ searchParams, employerId, employerUserId, candidateId, isEmployerAdmin })
    }
  }, [userFilterAlertParam, employerId, employerUserId, candidateId, isEmployerAdmin])

  const userFilterAlertTypeName = listType === 'job' ? 'job' : 'candidate'
  const availableAlerts =
    userFilterAlerts.filter(
      alert =>
        alert.filterType.toLowerCase() === userFilterAlertTypeName && Number(alert.recordCount) > 0,
    ) || []

  const handleSaveDialogClose = () => {
    setOpenSaveDialog(false)
  }

  const handleSaveDialogOpen = () => {
    dispatch(
      fetchTrackEvent({
        eventName: 'save_filter_lead_click',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
      }),
    )
    setOpenSaveDialog(true)
  }

  const handleMoreDialogClose = () => setOpenMoreDialog(false)
  const handleMoreDialogOpen = () => {
    dispatch(
      fetchTrackEvent({
        eventName: 'more_filters_lead_click',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
      }),
    )
    setOpenMoreDialog(true)
  }

  const handleSaveFilterDialogClose = () => setOpenSaveFilterDialog(false)
  const handleSaveFilterDialogOpen = () => setOpenSaveFilterDialog(true)

  // Calculate count of selected More Filters
  const getMoreFiltersCount = () => {
    let count = 0
    Object.keys(selectedFilters).forEach(key => {
      const filterDef = filterDefinitions.find(filter => filter.name === key)
      if (filterDef && filterDef.showMoreFilters && selectedFilters[key]?.selectedValues?.length > 0) {
        count += selectedFilters[key].selectedValues.length
      }
    })
    return count
  }

  const moreFiltersCount = getMoreFiltersCount()

  const handleFilterChange = selectedFilter => {
    const { filterDefinition, selectedValues } = selectedFilter

    const newSearchParams = filterDefinition.clearOthers
      ? new URLSearchParams()
      : new URLSearchParams(searchParams)

    if (selectedValues.length > 0) {
      const valueString = filterDefinition.isYesNo
        ? selectedValues[0].value === '1'
        : selectedValues.map(x => x.value).join('|')

      newSearchParams.set(filterDefinition.queryStringKey, valueString)
    } else {
      newSearchParams.delete(filterDefinition.queryStringKey)
    }

    setSearchParams(newSearchParams)

    setSelectedFilters(
      selectedValues.length > 0 ? updateSelectedFilters(selectedFilters, selectedFilter) : {},
    )
  }

  const handleClearAll = () => {
    const newSearchParams = new URLSearchParams(searchParams)
    filterDefinitions.forEach(filterDefinition => {
      if (filterDefinition.clearAll === true) {
        newSearchParams.delete(filterDefinition.queryStringKey)
      }
    })

    setSearchParams(newSearchParams)
    setSelectedFilters(currentFilters => {
      const updatedFilters = { ...currentFilters }
      Object.keys(updatedFilters).forEach(key => {
        if (filterDefinitions.find(filter => filter.name === key)) {
          delete updatedFilters[key]
        }
      })
      return updatedFilters
    })
  }

  const toggleShowAllFilters = () => {
    setShowAllFilters(!showAllFilters)
  }

  const toggleFilterSection = () => {
    setIsFilterSectionOpen(!isFilterSectionOpen)
  }

  const handleSaveLinkClick = event => {
    if (!signedIn) {
      event.preventDefault()
      handleSaveDialogOpen()
    } else {
      handleSaveFilterDialogOpen()
    }
  }

  const handleMoreLinkClick = event => {
    if (!signedIn) {
      event.preventDefault()
      handleMoreDialogOpen()
    } else {
      toggleShowAllFilters()
    }
  }

  const handleGroupFilterChange = ({ selectedValues, filterDefinition }) => {
    // Copy the current searchParams
    const newSearchParams = new URLSearchParams(searchParams)
    if (selectedValues.length > 0) {
      const valueString = filterDefinition.isYesNo
        ? selectedValues[0].value === '1'
        : selectedValues.map(x => x.value).join('|')
      if (valueString) {
        newSearchParams.set(filterDefinition.queryStringKey, valueString)
      } else {
        newSearchParams.delete(filterDefinition.queryStringKey)
      }
    } else {
      newSearchParams.delete(filterDefinition.queryStringKey)
    }

    setSearchParams(newSearchParams)
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterDefinition.name]: {
        filterDefinition,
        selectedValues,
      },
    }))
  }

  const renderFilters = filtersToRender => {
    const validFilters = filtersToRender.filter(
      filter => filter.filterValues && filter.filterValues.length > 0,
    )

    const { groupedFilters, ungroupedFilters } = validFilters.reduce(
      (acc, filter) => {
        if (filter.filterDefinition?.filterGroup) {
          const displayName = filterGroups.find(
            filterGroup => filterGroup.name === filter.filterDefinition.filterGroup,
          ).displayName
          const existingGroup = acc.groupedFilters.find(
            group => group.filterGroup === filter.filterDefinition?.filterGroup,
          )

          if (existingGroup) {
            existingGroup.filters.push(filter)
          } else {
            acc.groupedFilters.push({
              filterGroup: filter.filterDefinition.filterGroup,
              displayName: displayName,
              filters: [filter],
            })
          }
        } else {
          acc.ungroupedFilters.push(filter)
        }
        return acc
      },
      { groupedFilters: [], ungroupedFilters: [] },
    )

    const sortedFilters = [
      ...ungroupedFilters.filter(filter => !filter.filterDefinition.isYesNo),
      ...ungroupedFilters.filter(filter => filter.filterDefinition.isYesNo),
    ]

    return (
      <>
        <Grid container spacing={mobile ? 1 : 2} sx={{ 
          px: mobile ? 2 : 0,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}>
          {/* Render ungrouped filters */}
          {sortedFilters.map((filter, index) => {
            const selectedFilter = selectedFilters[filter.filterDefinition.name]
            const FilterComponent = mobile ? MobileFilterDropdown : DesktopFilterDropdown

            return (
              <Grid
                item
                key={`${filter.filterDefinition.name}-${index}`}
                xs={12}
                sm={mobile ? 12 : 6}
                md={2}
                sx={{
                  display: 'flex',
                  justifyContent: mobile ? 'stretch' : 'flex-start',
                  paddingTop: '8px',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                <FilterComponent
                  filterDefinition={filter.filterDefinition}
                  options={filter.filterValues}
                  checked={selectedFilter ? selectedFilter.selectedValues : null}
                  onChange={handleFilterChange}
                  styles={styles}
                  mobile={mobile}

                />
              </Grid>
            )
          })}

          {/* Render grouped filters */}
          <Grid item xs={12}
                sm={mobile ? 12 : 6}
                md={2}
                sx={{
                  display: 'flex',
                  justifyContent: mobile ? 'stretch' : 'flex-start',
                  paddingTop: '8px',
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}>
            {groupedFilters.map(groupedFilter => {
              const CompositeFilterComponent = mobile
                ? MobileCompositeFilterDropdown
                : DesktopCompositeFilterDropdown
              return (
                <CompositeFilterComponent
                  groupedFilter={groupedFilter}
                  onChange={handleGroupFilterChange}
                  selectedFilters={selectedFilters}
                  styles={styles}
                />
              )
            })}
          </Grid>
        </Grid>
      </>
    )
  }

  return (
    <>
      <Grid
        container
        ref={ref}
        justifyContent='center'
        sx={{
          overflow: 'hidden',
          padding: 0,
          position: 'relative',
          zIndex: 5,
        }}
      >
        {/* Always visible header with collapse toggle */}
        {mobile && (
          <Grid
            container
            sx={{
              backgroundColor: theme.palette.background.dark,
              padding: '12px 16px',
              marginTop: '56px', // Account for mobile top bar
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon sx={{ color: 'white' }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  Filters
                </Typography>
                {Object.keys(selectedFilters).length > 0 && (
                  <Box
                    sx={{
                      backgroundColor: theme.palette.action.selected,
                      borderRadius: '12px',
                      padding: '2px 8px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: theme.palette.common.white,
                    }}
                  >
                    {Object.keys(selectedFilters).length}
                  </Box>
                )}
              </Box>
              <IconButton
                onClick={toggleFilterSection}
                sx={{
                  color: theme.palette.common.white,
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    transform: isFilterSectionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                    fontSize: '24px',
                  }}
                />
              </IconButton>
            </Grid>
          </Grid>
        )}

        <Collapse in={isFilterSectionOpen}>
          <Grid
            container
            className={`filterWrapper ${styles.filterHeaderWrapper}`}
            sx={{ 
              overflow: 'hidden',
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              marginTop: mobile ? '-10px' : 0, // Reduce gap on mobile
            }}
          >
            <Grid
              container
              item
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{ 
                width: '100%',
                overflow: 'hidden',
                px: { xs: 2, md: 4 }
              }}
            >
              <FilterHeader
                listType={listType}
                filterValues={filterValues}
                handleFilterChange={handleFilterChange}
                toggleShowAllFilters={toggleShowAllFilters}
                selectedFilters={selectedFilters}
                filterTypes={filterTypes}
                showAllFilters={showAllFilters}
                mobile={mobile}
                isFilterSectionOpen={isFilterSectionOpen}
                toggleFilterSection={null} // Don't show collapse icon in FilterHeader anymore
              />
            </Grid>
          </Grid>
        <Grid
          container
          className={`filterWrapper ${styles.filterWrapper}`}
          sx={{
            padding: mobile ? '16px' : '10px 0px',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        >
          <Grid
            container
            item
            alignItems='center'
            justifyContent='space-between'
            spacing={mobile ? 0 : 2}
            mb={1}
            sx={{ 
              width: '100%',
              overflow: 'hidden', 
              padding: '0px',
              px: { xs: 1, md: 4 }
            }}
          >
            {/* Left-aligned Buttons */}
            <Grid item xs={mobile ? 12 : 'auto'} sm='auto'>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: mobile ? 'column' : 'row',
                alignItems: 'center', 
                gap: mobile ? '8px' : '12px',
                mb: mobile ? 1 : 0
              }}>
                <SavedFiltersButton
                  listType={listType}
                  filterTypes={filterTypes}
                />
                <TextField
                  onClick={handleMoreLinkClick}
                  size='small'
                  value={showAllFilters 
                    ? 'Hide Filters' 
                    : moreFiltersCount > 0 
                      ? `More Filters (${moreFiltersCount})` 
                      : 'More Filters'
                  }
                  InputProps={{
                    readOnly: true,
                    style: {
                      color: '#2A2A2A',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                    },
                    endAdornment: (
                      <ExpandMoreIcon
                        sx={{
                          fontSize: '20px',
                          color: '#2A2A2A',
                          transform: showAllFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                        }}
                      />
                    ),
                  }}
                  sx={{
                    width: mobile ? '100%' : '200px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#2A2A2A',
                      },
                      '&:hover fieldset': {
                        borderColor: '#2A2A2A',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2A2A2A',
                      },
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                    },
                    '& .MuiInputBase-input': {
                      color: '#2A2A2A',
                      fontSize: '0.95rem',
                    },
                  }}
                />
              </Box>
            </Grid>
            
            {availableAlerts.length > 0 && userFilterAlertParam !== null && (
              <Grid item xs={mobile ? 12 : 6} sm={mobile ? 12 : 6} md={6}>
                <AlertsButton availableAlerts={availableAlerts} />
              </Grid>
            )}

            {/* Right-aligned Buttons */}
            <Grid item xs={mobile ? 12 : 'auto'} sm='auto'>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: mobile ? '12px' : '20px',
                  justifyContent: 'center',
                }}
              >
                <Link
                  href='#'
                  onClick={handleClearAll}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#2A2A2A',
                    textDecoration: 'none !important',
                    fontSize: mobile ? '0.9rem' : '1.1rem',
                    whiteSpace: 'nowrap',
                    background: 'none !important',
                    backgroundSize: '0 !important',
                    gap: '5px',
                    '&:hover': {
                      textDecoration: 'none !important',
                      background: 'none !important',
                      backgroundSize: '0 !important',
                    },
                    '&:focus': {
                      textDecoration: 'none !important',
                      background: 'none !important',
                      backgroundSize: '0 !important',
                    },
                  }}
                >
                  <ClearIcon
                    sx={{
                      fontSize: mobile ? '16px' : '18px',
                      color: '#2A2A2A',
                    }}
                  />
                  Clear Filters
                </Link>
                <Link
                  href='#'
                  onClick={handleSaveLinkClick}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#2A2A2A',
                    textDecoration: 'none !important',
                    fontSize: mobile ? '0.9rem' : '1.1rem',
                    whiteSpace: 'nowrap',
                    background: 'none !important',
                    backgroundSize: '0 !important',
                    '&:hover': {
                      textDecoration: 'none !important',
                      background: 'none !important',
                      backgroundSize: '0 !important',
                    },
                    '&:focus': {
                      textDecoration: 'none !important',
                      background: 'none !important',
                      backgroundSize: '0 !important',
                    },
                  }}
                >
                  Save Filters
                  <SaveIcon
                    sx={{
                      marginLeft: '5px',
                      fontSize: mobile ? '16px' : '18px',
                      color: '#2A2A2A',
                    }}
                  />
                </Link>
              </Box>
            </Grid>
          </Grid>
          <Grid
            item
            container
            direction='row'
            alignItems='center'
            spacing={0}
            sx={{ 
              width: '100%',
              overflow: 'hidden',
              px: { xs: 2, md: 4 }
            }}
          >
            <Collapse in={showAllFilters} sx={{ width: '100%' }}>
              <Grid item container direction='row' alignItems='center' spacing={0}>
                {renderFilters(filters)}
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
        </Collapse>
      </Grid>
      
      <PhysicianSignUpDialog
        openDialog={openSaveDialog}
        handleDialogClose={handleSaveDialogClose}
        feature='save'
      />
      <PhysicianSignUpDialog
        openDialog={openMoreDialog}
        handleDialogClose={handleMoreDialogClose}
        feature='more'
      />
      <SaveFilterDialog
        openDialog={openSaveFilterDialog}
        handleDialogClose={handleSaveFilterDialogClose}
        selectedFilters={selectedFilters}
        filterTypes={filterTypes}
        listType={listType}
      />
    </>
  )
}
